/**
 * Admin Plans Hooks
 * ადმინისტრატორის გეგმების ჰუკები
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAdminPlans,
  getAdminPlan,
  createPlan,
  updatePlan,
  deletePlan,
  updateAdminPlanFeatures,
  type CreatePlanRequest,
  type UpdatePlanRequest,
  type UpdateAdminPlanFeaturesRequest,
} from '../features/api/adminPlans';

export function useAdminPlans() {
  const query = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: getAdminPlans,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    plans: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useAdminPlan(code: string | null) {
  const query = useQuery({
    queryKey: ['admin', 'plans', code],
    queryFn: () => getAdminPlan(code!),
    enabled: !!code,
    staleTime: 1000 * 60 * 5,
  });

  return {
    plan: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCreatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePlanRequest) => createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
    },
  });
}

export function useUpdatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ code, data }: { code: string; data: UpdatePlanRequest }) =>
      updatePlan(code, data),
    onSuccess: (_, { code }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans', code] });
    },
  });
}

export function useDeletePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) => deletePlan(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
    },
  });
}

export function useUpdateAdminPlanFeatures() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ code, data }: { code: string; data: UpdateAdminPlanFeaturesRequest }) =>
      updateAdminPlanFeatures(code, data),
    onSuccess: (_, { code }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans', code] });
    },
  });
}
